import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import * as moment from 'moment';
import { OPERATORS } from 'src/app/services/constants';

export type TleTrackPoint = {
  point: L.LatLngTuple;
  timeStamp?: string;
};

export type QualifiedTleTrackPoint = TleTrackPoint & {
  distanceKm: number;
};

@Injectable({
  providedIn: 'root',
})
export class TleHelperService {
  private getTrackPointTimestampValue(value: any): string | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      return trimmedValue || undefined;
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return new Date(value).toISOString();
    }

    return undefined;
  }

  extractSatelliteResults(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.satellites)) {
      return response.satellites;
    }

    if (Array.isArray(response?.response)) {
      return response.response;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.result)) {
      return response.result;
    }

    return [];
  }

  getSatelliteSensorIds(satellite: any): Array<string | number> {
    const sensorIds =
     satellite?.sensor_ids ??
      null;

    if (Array.isArray(sensorIds)) {
      return sensorIds.filter(
        (sensorId) => sensorId !== null && sensorId !== undefined && sensorId !== ''
      );
    }

    const legacySensorId = satellite?.sensorId ?? satellite?.sensor?.sensorId ?? null;

    if (legacySensorId === null || legacySensorId === undefined || legacySensorId === '') {
      return [];
    }

    return [legacySensorId];
  }

  getSatelliteNoradId(satellite: any): string | null {
    const noradId =
      satellite?.noradId ??
      null;

    if (noradId === null || noradId === undefined || noradId === '') {
      return null;
    }

    return String(noradId).trim() || null;
  }

  getSatelliteOperatorKey(satellite: any): string | null {
    const operatorKey = satellite?.operatorKey ?? null;

    return typeof operatorKey === 'string' && operatorKey.trim()
      ? operatorKey.trim()
      : null;
  }

  getOperatorColor(key: string): string {
    switch (key) {
      case OPERATORS.CAPELLA:
        return 'yellow';
      case OPERATORS.SATELLOGIC:
        return 'yellowgreen';
      case OPERATORS.UMBRA:
        return 'blue';
      case OPERATORS.HEAD:
        return 'greenyellow';
      case OPERATORS.STE:
        return 'blueviolet';
      case OPERATORS.STE_TELEOS:
        return 'mediumslateblue';
      case OPERATORS.AT21:
        return 'green';
      case OPERATORS.KOMPSAT:
        return 'cyan';
      case OPERATORS.USL:
        return 'indigo';
      case OPERATORS.ISI:
        return 'turquoise';
      case OPERATORS.AXELSPACE:
        return 'gold';
      case OPERATORS.GHG:
        return '#25aae0';
      case OPERATORS.ORORATECH:
        return '#008B8B';
      case OPERATORS.SATVU:
        return 'steelblue';
      case OPERATORS.SATELLITEVU_VANZYL:
        return 'deepskyblue';
      case OPERATORS.BLACKSKY:
        return 'slategray';
      case OPERATORS.WYVERN:
        return 'brown';
      case OPERATORS.SPIRE:
        return 'plum';
      case OPERATORS.CLYDE:
        return '#BC8F8F';
      case OPERATORS.SYNSPECTIVE:
        return 'green';
      case OPERATORS.GOKTURK:
        return '#9ce49c';
      case OPERATORS.LBAND:
        return 'violet';
      case OPERATORS.ECURS:
      case OPERATORS.ECURS_L:
        return 'aqua';
      case OPERATORS.ESPER:
        return 'deeppink';
      case OPERATORS.PIXXEL:
        return 'orangered';
      case OPERATORS.OSK:
        return 'darkorange';
      case OPERATORS.AZISTA:
        return 'mediumseagreen';
      case OPERATORS.FIREFLIES:
        return 'salmon';
      case OPERATORS.SDS:
        return 'darkmagenta';
      case OPERATORS.ORBVIEW:
        return 'darkslategray';
      case OPERATORS.GEOSAT:
        return 'coral';
      default:
        return 'royalblue';
    }
  }

  getSatellitePayloadTimestamp(timeStamp?: string, fallback?: string): string | null {
    const rawValue =
      typeof timeStamp === 'string' && timeStamp.trim()
        ? timeStamp.trim()
        : typeof fallback === 'string' && fallback.trim()
          ? fallback.trim()
          : null;

    return rawValue || null;
  }

  getSatelliteCaptureWidthKm(satellite: any): number | null {
    const swath = satellite?.swath ?? null;

    if (swath === null || swath === undefined || swath === '') {
      return null;
    }

    const normalizedSwath = Number(
      typeof swath === 'string' ? swath.replace(/,/g, '') : swath
    );
    if (!Number.isFinite(normalizedSwath) || normalizedSwath <= 0) {
      return null;
    }

    return normalizedSwath;
  }

  getQualifyingSatelliteTrackPoints(
    trackPoints: TleTrackPoint[],
    referencePoint: L.LatLngTuple,
    captureWidthKm: number
  ): QualifiedTleTrackPoint[] {
    return trackPoints
      .map((trackPoint) => ({
        ...trackPoint,
        distanceKm: this.getDistanceKm(trackPoint.point, referencePoint),
      }))
      .filter(
        (trackPoint) =>
          Number.isFinite(trackPoint.distanceKm) &&
          trackPoint.distanceKm <= captureWidthKm
      )
      .sort((left, right) => left.distanceKm - right.distanceKm);
  }

  getNearestQualifyingSatelliteTrackPoint(
    trackPoints: TleTrackPoint[],
    referencePoint: L.LatLngTuple,
    captureWidthKm: number
  ): QualifiedTleTrackPoint | null {
    const [nearestTrackPoint] = this.getQualifyingSatelliteTrackPoints(
      trackPoints,
      referencePoint,
      captureWidthKm
    );

    return nearestTrackPoint || null;
  }

  getPassGroupTimestamp(
    trackPoints: TleTrackPoint[],
    fallback?: string
  ): string {
    const pointTimestamp = trackPoints
      .map((trackPoint) => this.getSatellitePayloadTimestamp(trackPoint?.timeStamp))
      .find((value): value is string => !!value);

    return pointTimestamp || this.formatSatellitePassTimestamp(undefined, fallback);
  }

  getSatelliteTrackPoints(positions: any): TleTrackPoint[] {
    if (!Array.isArray(positions)) {
      return [];
    }

    return positions
      .map((entry: any): TleTrackPoint | null => {
        if (Array.isArray(entry) && entry.length >= 2) {
          return {
            point: [entry[1], this.normalizeLongitude(entry[0])] as L.LatLngTuple,
            timeStamp: this.getTrackPointTimestampValue(entry[2]),
          };
        }

        const coordinates = Array.isArray(entry?.coordinates)
          ? entry.coordinates
          : null;
        if (!coordinates || coordinates.length < 2) {
          return null;
        }

        return {
          point: [
            coordinates[1],
            this.normalizeLongitude(coordinates[0]),
          ] as L.LatLngTuple,
          timeStamp: this.getTrackPointTimestampValue(
            entry?.timeStamp || entry?.timestamp
          ),
        };
      })
      .filter((entry: TleTrackPoint | null): entry is TleTrackPoint => !!entry);
  }

  getNearestSatelliteTrackPoint(
    trackPoints: TleTrackPoint[],
    referencePoint: L.LatLngTuple
  ): TleTrackPoint | null {
    let nearestPoint: TleTrackPoint | null = null;
    let minDistance = Infinity;

    for (const trackPoint of trackPoints) {
      const distance = this.getDistanceKm(trackPoint.point, referencePoint);
      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = trackPoint;
      }
    }

    return nearestPoint;
  }

  getNearestDistanceToPassKm(
    trackPoints: TleTrackPoint[],
    referencePoint: L.LatLngTuple
  ): number {
    if (!trackPoints.length) {
      return Infinity;
    }

    if (trackPoints.length === 1) {
      return this.getDistanceKm(trackPoints[0].point, referencePoint);
    }

    let minDistance = Infinity;

    for (let index = 0; index < trackPoints.length - 1; index++) {
      const distance = this.getDistanceFromPointToSegmentKm(
        referencePoint,
        trackPoints[index].point,
        trackPoints[index + 1].point
      );
      if (distance < minDistance) {
        minDistance = distance;
      }
    }

    return minDistance;
  }

  getDistanceFromPointToSegmentKm(
    point: L.LatLngTuple,
    segmentStart: L.LatLngTuple,
    segmentEnd: L.LatLngTuple
  ): number {
    const latKm = 110.574;
    const lonKm =
      111.32 *
      Math.cos(this.toRad((point[0] + segmentStart[0] + segmentEnd[0]) / 3));
    const ax = (segmentStart[1] - point[1]) * lonKm;
    const ay = (segmentStart[0] - point[0]) * latKm;
    const bx = (segmentEnd[1] - point[1]) * lonKm;
    const by = (segmentEnd[0] - point[0]) * latKm;
    const abx = bx - ax;
    const aby = by - ay;
    const segmentLengthSquared = abx * abx + aby * aby;

    if (segmentLengthSquared === 0) {
      return Math.sqrt(ax * ax + ay * ay);
    }

    const projection = Math.max(
      0,
      Math.min(1, (-(ax * abx + ay * aby)) / segmentLengthSquared)
    );
    const closestX = ax + abx * projection;
    const closestY = ay + aby * projection;

    return Math.sqrt(closestX * closestX + closestY * closestY);
  }

  formatSatellitePassTimestamp(timeStamp?: string, fallback?: string): string {
    return this.getSatellitePayloadTimestamp(timeStamp, fallback) || '';
  }

  
  formatSatellitePassTooltipTimestamp(timeStamp?: string, fallback?: string): string {
    const rawValue = timeStamp || fallback;
    if (!rawValue) {
      return '';
    }

    const parsed = moment.utc(rawValue).format('DD/MM/YYYY HH:mm:ss [UTC]');
    return parsed || rawValue;
  }

  getDistanceKm(a: L.LatLngTuple, b: L.LatLngTuple): number {
    const radius = 6371;
    const deltaLat = this.toRad(b[0] - a[0]);
    const deltaLon = this.toRad(b[1] - a[1]);
    const lat1 = this.toRad(a[0]);
    const lat2 = this.toRad(b[0]);

    const haversine =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);

    return radius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  normalizeLongitude(longitude: number): number {
    return ((((longitude + 180) % 360) + 360) % 360) - 180;
  }

  private toRad(value: number): number {
    return (value * Math.PI) / 180;
  }
}